import { useEffect, useRef, useState } from "react";
import { AlertCircle, Bookmark, Check, Copy, Plus, Terminal } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { log } from "../../lib/log";
import { Card, Field, METHODS } from "./_shared";

export function CardsTab({ onChange, snippet, bookmarklet }) {
  const { apiFetch } = useAuth();
  const [form, setForm] = useState({ name: "", host: "", category: "", methods: [] });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const bmRef = useRef(null);

  useEffect(() => {
    if (bookmarklet && bmRef.current) bmRef.current.setAttribute("href", bookmarklet);
  }, [bookmarklet]);

  const toggleMethod = (m) => {
    setForm((f) => {
      const has = f.methods.includes(m);
      return { ...f, methods: has ? f.methods.filter((x) => x !== m) : [...f.methods, m] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.host.trim()) return;
    if (form.methods.length === 0) {
      setResult({ ok: false, msg: "Pick at least one payment method" });
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const d = await apiFetch("/api/amex/add", {
        method: "POST",
        body: JSON.stringify(form),
      });
      if (d.ok) {
        setResult({ ok: true, msg: `${d.inserted ? "Added" : "Updated"}: ${d.host}` });
        setForm({ name: "", host: "", category: "", methods: [] });
        onChange?.();
      } else {
        setResult({ ok: false, msg: JSON.stringify(d) });
      }
    } catch (err) {
      setResult({ ok: false, msg: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const copySnippet = async () => {
    try { await navigator.clipboard.writeText(snippet || ""); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch (e) { log.warn("[admin] clipboard copy failed:", e?.message); }
  };

  return (
    <div className="space-y-px">
      <Card title="ADD A MERCHANT" subtitle="Add any site and the payment methods it accepts." accent="#10B981" testid="cards-add-card">
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <Field label="Merchant name" testid="add-name">
            <input
              required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Komplett"
              data-testid="add-name-input"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#10B981] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors"
            />
          </Field>
          <Field label="Website host" testid="add-host">
            <input
              required value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              placeholder="komplett.no"
              data-testid="add-host-input"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#10B981] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] font-mono transition-colors"
            />
          </Field>
          <Field label="Category (optional)" testid="add-category">
            <input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Elektronikk & teknologi"
              data-testid="add-category-input"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#10B981] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors"
            />
          </Field>
          <Field label="Accepted payment methods" testid="add-methods">
            <div className="flex gap-2 flex-wrap">
              {METHODS.map((m) => {
                const active = form.methods.includes(m.key);
                return (
                  <button
                    type="button" key={m.key}
                    onClick={() => toggleMethod(m.key)}
                    data-testid={`add-method-${m.key}`}
                    className={`px-3 py-2 text-[10px] tracking-[0.18em] font-bold uppercase border transition-all ${active ? (m.key === "klarna" ? "text-black" : (m.key === "applepay" ? "text-white" : "text-[var(--srs-ink)]")) : "text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)]"}`}
                    style={{
                      borderColor: active ? m.color : "#27272A",
                      background: active ? m.color : "transparent",
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </Field>
          <div className="sm:col-span-2 flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit" disabled={submitting}
              data-testid="add-submit-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#10B981] hover:bg-[#0e9e6e] disabled:bg-[var(--srs-line-soft)] disabled:text-[var(--srs-ink-faint)] text-black text-xs tracking-[0.25em] font-bold uppercase transition-colors"
            >
              <Plus className="w-4 h-4" /> {submitting ? "Adding…" : "Add to database"}
            </button>
            {result && (
              <span data-testid="add-result" className={`text-xs tracking-wide ${result.ok ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                {result.ok ? "✓ " : "✗ "}{result.msg}
              </span>
            )}
          </div>
        </form>
      </Card>

      <details className="border border-[var(--srs-line)] bg-[var(--srs-surface)]">
        <summary className="cursor-pointer p-5 text-[11px] tracking-[0.25em] font-bold text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] transition-colors flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5 text-[#10B981]" /> ADVANCED · BULK IMPORT FROM EUROBONUSGUIDEN
        </summary>
        <div className="p-5 pt-0 space-y-4">
          <p className="text-xs text-[var(--srs-ink-dim)] leading-relaxed max-w-2xl">
            For batch-importing hundreds of merchants from{" "}
            <a className="text-[var(--srs-ink)] underline decoration-[#10B981] underline-offset-4" href="https://eurobonusguiden.no/amex-brukersteder/" target="_blank" rel="noreferrer">
              eurobonusguiden.no
            </a>. Open the site, paste the snippet in DevTools Console, press Enter.
          </p>
          <div className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)]">
            <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--srs-line)] bg-[var(--srs-surface)]">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-dim)]">
                <Terminal className="w-3 h-3 text-[#10B981]" /> CONSOLE.PASTE
              </div>
              <button onClick={copySnippet} data-testid="copy-snippet-btn"
                className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#10B981] hover:bg-[#0e9e6e] text-black text-[10px] tracking-[0.2em] font-bold uppercase transition-colors">
                {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
            </div>
            <pre data-testid="snippet-code" className="text-[11px] leading-relaxed text-[var(--srs-ink)] p-4 max-h-[200px] overflow-auto whitespace-pre-wrap break-all">
              {snippet || "Loading snippet…"}
            </pre>
          </div>
          <div className="flex items-start gap-2 text-[11px] text-[var(--srs-ink-faint)] leading-relaxed">
            <AlertCircle className="w-3.5 h-3.5 text-[#F59E0B] shrink-0 mt-0.5" />
            <span>Contains your private import token. Don't share publicly.</span>
          </div>
          {bookmarklet && (
            <a ref={bmRef} draggable onClick={(e) => e.preventDefault()}
              data-testid="bookmarklet-link"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--srs-line-soft)] hover:bg-[var(--srs-line)] text-[var(--srs-ink)] text-[10px] tracking-[0.25em] font-bold uppercase cursor-grab active:cursor-grabbing">
              <Bookmark className="w-3 h-3" /> Drag to bookmarks bar
            </a>
          )}
        </div>
      </details>
    </div>
  );
}
